import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RichContentEditor from './RichContentEditor';
import { API_ENDPOINTS, getImageUrl } from '../config/api';
import { AdminPageHeader, AdminPageShell, NoticePanel, StatCard } from './AdminPageUI';

const ManageSubjectsTopicsLessons = () => {
  const navigate = useNavigate();

  // === Core Data States ===
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [lessons, setLessons] = useState([]);

  // === UI States ===
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // === Form States ===
  const [formData, setFormData] = useState({
    type: 'subject',
    id: '',
    name: '',
    title: '',
    description: '',
    content: '',
    subjectId: '',
    topicId: '',
    className: '',
    images: [], // Array of uploaded image objects
    featuredImage: null, // Selected featured image
    imageDescriptions: {} // Descriptions for images
  });

  // === Image Upload States ===
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [academicContext, setAcademicContext] = useState({
    academicSession: '2025/2026',
    term: 'First Term',
    schoolWeek: 1
  });

  // === Statistics ===
  const [stats, setStats] = useState({
    totalSubjects: 0,
    totalTopics: 0,
    totalLessons: 0,
    totalContentItems: 0
  });

  // ========================= INITIALIZATION =========================
  useEffect(() => {
    fetchClasses();
    fetchAcademicContext();
  }, []);

  const fetchAcademicContext = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/academic-context`);
      if (response.ok) {
        setAcademicContext(await response.json());
      }
    } catch (error) {
      console.error('Error loading academic context:', error);
    }
  };

  const saveAcademicContext = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/academic-context`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(academicContext)
      });
      if (response.ok) {
        setAcademicContext(await response.json());
        alert('Academic context saved');
      } else {
        alert('Failed to save academic context');
      }
    } catch (error) {
      alert('Failed to save academic context');
    }
  };

  useEffect(() => {
    if (selectedClass) {
      console.log('Selected class changed to:', selectedClass);
      loadAllData();
    }
  }, [selectedClass]);

  // ========================= DATA FETCHING =========================
  const fetchClasses = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CLASSES);
      const classesData = await response.json();
      setClasses(classesData);
      if (classesData.length > 0 && !selectedClass) {
        setSelectedClass(classesData[0]);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const loadAllData = async () => {
    if (!selectedClass) return;

    setLoading(true);
    try {
      const [subjectsRes, topicsRes, lessonsRes] = await Promise.all([
        fetch(API_ENDPOINTS.SUBJECTS),
        fetch(API_ENDPOINTS.TOPICS),
        fetch(API_ENDPOINTS.LESSONS)
      ]);

      const subjectsData = await subjectsRes.json();
      const topicsData = await topicsRes.json();
      const lessonsData = await lessonsRes.json();

      // Filter by selected class (case-insensitive)
      const classSubjects = subjectsData.filter(s => s.className?.toLowerCase() === selectedClass.toLowerCase());
      const classTopics = topicsData.filter(t => t.className?.toLowerCase() === selectedClass.toLowerCase());
      const classLessons = lessonsData.filter(l => l.className?.toLowerCase() === selectedClass.toLowerCase());

      setSubjects(classSubjects);
      setTopics(classTopics);
      setLessons(classLessons);

      // Calculate and update statistics immediately
      const newStats = {
        totalSubjects: classSubjects.length,
        totalTopics: classTopics.length,
        totalLessons: classLessons.length,
        totalContentItems: classSubjects.length + classTopics.length + classLessons.length
      };

      setStats(newStats);
      console.log('Updated statistics:', newStats); // Debug log

    } catch (error) {
      console.error('Error loading data:', error);
      // Reset stats on error
      setStats({
        totalSubjects: 0,
        totalTopics: 0,
        totalLessons: 0,
        totalContentItems: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // ========================= TREE MANAGEMENT =========================
  const toggleSubjectExpansion = (subjectId) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectId)) {
      newExpanded.delete(subjectId);
    } else {
      newExpanded.add(subjectId);
    }
    setExpandedSubjects(newExpanded);
  };

  const getTopicsForSubject = (subjectId) => {
    return topics.filter(topic => topic.subjectId === subjectId);
  };

  const getLessonsForTopic = (topicId) => {
    return lessons.filter(lesson => lesson.topicId === topicId);
  };

  const getSubjectStats = (subjectId) => {
    const subjectTopics = getTopicsForSubject(subjectId);
    const subjectLessons = lessons.filter(lesson =>
      subjectTopics.some(topic => topic.id === lesson.topicId)
    );
    return {
      topics: subjectTopics.length,
      lessons: subjectLessons.length
    };
  };

  // Debug: Log data relationships
  const debugDataRelationships = () => {
    console.log('=== DATA RELATIONSHIPS DEBUG ===');
    console.log('Selected class:', selectedClass);
    console.log('Subjects:', subjects.length, subjects);
    console.log('Topics:', topics.length, topics);
    console.log('Lessons:', lessons.length, lessons);

    subjects.forEach(subject => {
      const subjectTopics = getTopicsForSubject(subject.id);
      console.log(`Subject "${subject.name}" (${subject.id}) has ${subjectTopics.length} topics:`, subjectTopics.map(t => `${t.name} (${t.subjectId})`));
    });

    topics.forEach(topic => {
      const topicLessons = getLessonsForTopic(topic.id);
      console.log(`Topic "${topic.name}" (${topic.id}) has ${topicLessons.length} lessons:`, topicLessons.map(l => `${l.title} (${l.topicId})`));
    });

    console.log('=== END DEBUG ===');
  };

  // Call debug on data load
  React.useEffect(() => {
    if (subjects.length > 0 || topics.length > 0 || lessons.length > 0) {
      debugDataRelationships();
    }
  }, [subjects, topics, lessons]);

  // ========================= CRUD OPERATIONS =========================
  // ========================= IMAGE UPLOAD FUNCTIONS =========================
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    try {
      const formDataUpload = new FormData();
      Array.from(files).forEach(file => {
        formDataUpload.append('images', file);
      });

      const response = await fetch(`${API_ENDPOINTS.LESSONS}/upload-images`, {
        method: 'POST',
        body: formDataUpload
      });

      const result = await response.json();

      if (response.ok) {
        // Add uploaded images to form data
        const newImages = [...formData.images, ...result.files];
        setFormData(prev => ({
          ...prev,
          images: newImages
        }));

        // Create previews for display
        const newPreviews = result.files.map(file => ({
          url: getImageUrl(file.url),
          filename: file.filename,
          description: ''
        }));
        setImagePreviews(prev => [...prev, ...newPreviews]);

        alert(`${result.files.length} image(s) uploaded successfully!`);
      } else {
        alert(`Upload failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);

    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
    setImagePreviews(newPreviews);
  };

  const setFeaturedImage = (index) => {
    setFormData(prev => ({
      ...prev,
      featuredImage: prev.images[index]?.url || null
    }));
  };

  const updateImageDescription = (index, description) => {
    const newDescriptions = {
      ...formData.imageDescriptions,
      [formData.images[index]?.filename]: description
    };
    setFormData(prev => ({
      ...prev,
      imageDescriptions: newDescriptions
    }));
  };

  const openAddModal = (type, parentId = null) => {
    setFormData({
      type,
      id: '',
      name: '',
      title: '',
      description: '',
      content: '',
      subjectId: type === 'topic' ? parentId : '',
      topicId: type === 'lesson' ? parentId : '',
      className: selectedClass,
      images: [],
      featuredImage: null,
      imageDescriptions: {}
    });
    setImagePreviews([]);
    setSelectedImages([]);
    setShowAddModal(true);
  };

  const openEditModal = (item, type) => {
    let subjectId = item.subjectId || '';

    // For lessons, we need to find the subjectId from subjectName since lessons store subjectName
    if (type === 'lesson' && item.subjectName && !item.subjectId) {
      const subject = subjects.find(s => s.name === item.subjectName);
      subjectId = subject ? subject.id : '';
    }

    setFormData({
      type,
      id: item.id,
      name: item.name || '',
      title: item.title || '',
      description: item.description || '',
      content: item.content || '',
      subjectId: subjectId,
      topicId: item.topicId || '',
      className: item.className || selectedClass
    });
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      // Validation
      if (formData.type === 'subject' && !formData.name.trim()) {
        alert('Subject name is required');
        return;
      }

      if (formData.type === 'topic') {
        if (!formData.subjectId) {
          alert('Please select a subject for this topic');
          return;
        }
        if (!formData.name.trim()) {
          alert('Topic name is required');
          return;
        }
      }

      if (formData.type === 'lesson') {
        if (!formData.subjectId) {
          alert('Please select a subject for this lesson');
          return;
        }
        if (!formData.topicId) {
          alert('Please select a topic for this lesson');
          return;
        }
        if (!formData.title.trim()) {
          alert('Lesson title is required');
          return;
        }
      }

      let endpoint = '';
      let payload = {};

      if (formData.type === 'subject') {
        endpoint = formData.id
          ? `${API_ENDPOINTS.SUBJECTS}/${formData.id}`
          : API_ENDPOINTS.SUBJECTS;
        payload = {
          id: formData.id || `subject-${Date.now()}`,
          className: selectedClass,
          name: formData.name.trim()
        };
      } else if (formData.type === 'topic') {
        endpoint = formData.id
          ? `${API_ENDPOINTS.TOPICS}/${formData.id}`
          : API_ENDPOINTS.TOPICS;
        payload = {
          id: formData.id || `topic-${Date.now()}`,
          className: selectedClass,
          subjectId: formData.subjectId,
          name: formData.name.trim()
        };
      } else if (formData.type === 'lesson') {
        endpoint = formData.id
          ? `${API_ENDPOINTS.LESSONS}/${formData.id}`
          : API_ENDPOINTS.LESSONS;
        const subject = subjects.find(s => s.id === formData.subjectId);
        payload = {
          id: formData.id || `lesson-${Date.now()}`,
          className: selectedClass,
          subjectName: subject?.name || '',
          subjectId: formData.subjectId || '',
          topicId: formData.topicId,
          title: formData.title.trim(),
          content: formData.content?.trim() || `Content for ${formData.title}`,
          images: formData.images,
          featuredImage: formData.featuredImage,
          imageDescriptions: formData.imageDescriptions
        };
      }

      console.log('Sending payload:', payload); // Debug log

      const method = formData.id ? 'PUT' : 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();
      console.log('Response:', response.status, responseData); // Debug log

      if (response.ok) {
        setShowAddModal(false);
        setShowEditModal(false);
        setFormData({
          type: 'subject', id: '', name: '', title: '', description: '',
          content: '', subjectId: '', topicId: '', className: ''
        });
        loadAllData();
        alert(`${formData.type.charAt(0).toUpperCase() + formData.type.slice(1)} saved successfully!`);
      } else {
        alert(`Failed to save ${formData.type}: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving item:', error);
      alert(`Failed to save ${formData.type}: ${error.message}`);
    }
  };

  const handleDelete = async (item, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      let endpoint = '';
      if (type === 'subject') endpoint = `${API_ENDPOINTS.SUBJECTS}/${item.id}`;
      else if (type === 'topic') endpoint = `${API_ENDPOINTS.TOPICS}/${item.id}`;
      else if (type === 'lesson') endpoint = `${API_ENDPOINTS.LESSONS}/${item.id}`;

      const response = await fetch(endpoint, { method: 'DELETE' });

      if (response.ok) {
        loadAllData();
      } else {
        alert('Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  // ========================= FILTERING & SEARCH =========================
  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTopics = topics.filter(topic =>
    topic.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLessons = lessons.filter(lesson =>
    lesson.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lesson.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ========================= RENDERING =========================
  if (loading && !subjects.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading content management...</div>
      </div>
    );
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        eyebrow="Curriculum"
        title="Manage Subjects & Lessons"
        description="Build class curriculum from subjects to topics to rich lesson content with images and structured materials."
        actions={(
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700"
            >
              Back to Dashboard
            </button>
        )}
      />

        <div className="bg-white border border-cyan-200 rounded-xl shadow-sm p-5 mb-5">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-bold text-slate-700 mb-2">Academic Session</label>
              <input
                type="text"
                value={academicContext.academicSession || ''}
                onChange={(e) => setAcademicContext({ ...academicContext, academicSession: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder="2025/2026"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-bold text-slate-700 mb-2">Term</label>
              <select
                value={academicContext.term || 'First Term'}
                onChange={(e) => setAcademicContext({ ...academicContext, term: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
              >
                <option>First Term</option>
                <option>Second Term</option>
                <option>Third Term</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-bold text-slate-700 mb-2">School Week</label>
              <input
                type="number"
                min="1"
                value={academicContext.schoolWeek || 1}
                onChange={(e) => setAcademicContext({ ...academicContext, schoolWeek: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
              />
            </div>
            <button
              onClick={saveAcademicContext}
              className="bg-cyan-700 text-white px-5 py-2 rounded-lg font-bold hover:bg-cyan-800"
            >
              Save Academic Context
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            New subjects, topics, lessons, timetable entries, progress records, and exams use this active session, term, and school week.
          </p>
        </div>

        {/* Class Selection & Search */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Content</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search subjects, topics, lessons..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-end space-x-2">
              <button
                onClick={() => openAddModal('subject')}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                + Add Subject
              </button>
              <button
                onClick={() => openAddModal('topic')}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                + Add Topic
              </button>
              <button
                onClick={() => openAddModal('lesson')}
                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
              >
                + Add Lesson
              </button>
            </div>
          </div>
        </div>

        {(stats.totalTopics === 0 || stats.totalLessons === 0) && (
          <NoticePanel title="Curriculum readiness" tone="amber">
            Add topics and lessons under each subject so teachers and students can navigate the learning path clearly.
          </NoticePanel>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Subjects" value={stats.totalSubjects} tone="sky" detail={selectedClass || 'Selected class'} />
          <StatCard label="Topics" value={stats.totalTopics} tone="emerald" detail="Organized under subjects" />
          <StatCard label="Lessons" value={stats.totalLessons} tone="violet" detail="Student-facing materials" />
          <StatCard label="Total Items" value={stats.totalContentItems} tone="amber" detail="Curriculum records" />
        </div>

        {/* Content Tree */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="border-b px-6 py-4">
            <h2 className="text-xl font-bold text-gray-900">Content Structure</h2>
            <p className="text-sm text-gray-600 mt-1">Organize your curriculum with subjects, topics, and lessons</p>
          </div>

          <div className="p-6">
            {subjects.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">Books</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Content Yet</h3>
                <p className="text-gray-600 mb-4">Start building your curriculum by adding subjects</p>
                <button
                  onClick={() => openAddModal('subject')}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
                >
                  Create First Subject
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredSubjects.map((subject) => (
                  <div key={subject.id} className="border border-gray-200 rounded-lg">
                    {/* Subject Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-blue-50 border-b border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
                      onClick={() => toggleSubjectExpansion(subject.id)}
                    >
                      <div className="flex items-center space-x-3">
                        <button className="text-blue-600 hover:text-blue-800">
                          {expandedSubjects.has(subject.id) ? '▼' : '▶'}
                        </button>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            Books
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-800">{subject.name}</h3>
                            <p className="text-sm text-gray-600">
                              {getSubjectStats(subject.id).topics} topics ? {getSubjectStats(subject.id).lessons} lessons
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(subject, 'subject');
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(subject, 'subject');
                          }}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Topics */}
                    {expandedSubjects.has(subject.id) && (
                      <div className="ml-8 space-y-1">
                        {getTopicsForSubject(subject.id).map((topic) => (
                          <div key={topic.id} className="border-l-2 border-green-200 ml-4">
                            <div className="flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 transition-colors">
                              <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">
                                  Applications
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-800">{topic.name}</h4>
                                  <p className="text-sm text-gray-600">
                                    {getLessonsForTopic(topic.id).length} lessons
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => openAddModal('lesson', topic.id)}
                                  className="text-green-600 hover:text-green-800 text-sm"
                                >
                                  + Lesson
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(topic, 'topic');
                                  }}
                                  className="text-green-600 hover:text-green-800 p-1"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(topic, 'topic');
                                  }}
                                  className="text-red-600 hover:text-red-800 p-1"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>

                            {/* Lessons */}
                            <div className="ml-8 space-y-1">
                              {getLessonsForTopic(topic.id).map((lesson) => (
                                <div key={lesson.id} className="flex items-center justify-between p-2 bg-white hover:bg-gray-50 transition-colors border-l-2 border-purple-200 ml-4">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs">
                                      Study
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-gray-800">{lesson.title}</span>
                                      {lesson.images && lesson.images.length > 0 && (
                                        <div className="flex items-center space-x-1">
                                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                          <span className="text-xs text-gray-500">{lesson.images.length}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditModal(lesson, 'lesson');
                                      }}
                                      className="text-purple-600 hover:text-purple-800 p-1"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(lesson, 'lesson');
                                      }}
                                      className="text-red-600 hover:text-red-800 p-1"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}

                        {/* Add Topic Button */}
                        <div className="ml-4 mt-2">
                          <button
                            onClick={() => openAddModal('topic', subject.id)}
                            className="text-green-600 hover:text-green-800 text-sm flex items-center space-x-1"
                          >
                            <span>+</span>
                            <span>Add Topic</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">
              Add {formData.type.charAt(0).toUpperCase() + formData.type.slice(1)}
            </h3>

            {formData.type === 'subject' && (
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Subject Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            )}

            {formData.type === 'topic' && (
              <div className="space-y-4">
                <select
                  value={formData.subjectId}
                  onChange={(e) => setFormData({...formData, subjectId: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>{subject.name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Topic Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            )}

            {formData.type === 'lesson' && (
              <div className="space-y-4">
                <select
                  value={formData.subjectId}
                  onChange={(e) => setFormData({...formData, subjectId: e.target.value, topicId: ''})}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>{subject.name}</option>
                  ))}
                </select>
                <select
                  value={formData.topicId}
                  onChange={(e) => setFormData({...formData, topicId: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Topic</option>
                  {topics.filter(t => t.subjectId === formData.subjectId).map(topic => (
                    <option key={topic.id} value={topic.id}>{topic.name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Lesson Title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Content</label>
                  <RichContentEditor
                    value={formData.content}
                    onChange={(content) => setFormData({...formData, content})}
                    placeholder="Start typing your lesson content or paste images directly..."
                  />
                </div>

                {/* Image Upload Section */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Lesson Images</h4>

                  {/* File Input */}
                  <div className="mb-4">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files)}
                      disabled={uploadingImages}
                      className="w-full border rounded px-3 py-2"
                    />
                    {uploadingImages && (
                      <p className="text-sm text-blue-600 mt-1">Uploading images...</p>
                    )}
                  </div>

                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium text-gray-700">Uploaded Images:</h5>
                      <div className="grid grid-cols-2 gap-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="border rounded p-2">
                            <img
                              src={preview.url}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-20 object-cover rounded mb-2"
                            />
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => setFeaturedImage(index)}
                                className={`text-xs px-2 py-1 rounded ${
                                  formData.featuredImage === formData.images[index]?.url
                                    ? 'bg-yellow-500 text-white'
                                    : 'bg-gray-200 text-gray-700'
                                }`}
                              >
                                {formData.featuredImage === formData.images[index]?.url ? 'Featured' : 'Set Featured'}
                              </button>
                              <button
                                onClick={() => removeImage(index)}
                                className="text-red-500 text-xs hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                            <input
                              type="text"
                              placeholder="Image description (optional)"
                              value={formData.imageDescriptions[formData.images[index]?.filename] || ''}
                              onChange={(e) => updateImageDescription(index, e.target.value)}
                              className="w-full text-xs border rounded px-2 py-1 mt-2"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Save
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">
              Edit {formData.type.charAt(0).toUpperCase() + formData.type.slice(1)}
            </h3>

            {formData.type === 'subject' && (
              <input
                type="text"
                placeholder="Subject Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border rounded px-3 py-2"
              />
            )}

            {formData.type === 'topic' && (
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Topic Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            )}

            {formData.type === 'lesson' && (
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Lesson Title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Content</label>
                  <RichContentEditor
                    value={formData.content}
                    onChange={(content) => setFormData({...formData, content})}
                    placeholder="Start typing your lesson content or paste images directly..."
                  />
                </div>
              </div>
            )}

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Update
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default ManageSubjectsTopicsLessons;



